import { DataTypeProvider, SelectionStateProps, TableColumnWidthInfo } from '@devexpress/dx-react-grid'
import { Box, createStyles, makeStyles } from '@material-ui/core'
import { UserV1DocumentsGetRequest } from '@noco/http-client/lib/noco'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Colors, DynamicMuiIcon, Typography } from 'src/components/atoms'
import { Layout } from 'src/components/commons'
import {
  ModalAlertShare,
  ModalDocumentRegister,
  ModalFolderCreate,
  ModalFolderDelete,
  ModalFolderEdit,
  ModalSelectFolder,
  PopupTemplate
} from 'src/components/modals'
import { SelectTableRow, CustomButtonListType } from 'src/components/molecules'
import { Header, SubSideFolderList, Table, TableColumn, TableProps, useTablePagination } from 'src/components/organisms'
import { useListDocumentFolders, useMoveDocumentsToFolder } from 'src/fixtures/modules/documentFolder/hooks'
import { useFeatureFlag as useFolderFeatureFlag } from 'src/fixtures/modules/user/hooks'
import { DocumentFolder } from 'src/fixtures/types/folder'
import {
  DatetimeCell,
  DocumentTitleCell,
  FolderNameCell,
  LabelCell,
  NameCell,
  PublicationStatusCell,
  CountOfContactFromEmailCell,
  CountOfViewedByContactFromEmailCell,
  RateOfViewedByContactFromEmailCell,
  CountOfViewedByPublicLinkCell,
  SecondOfAverageBrowsingTimeCell,
  CountOfOpportunityCell,
  RateOfOpportunityCell,
  CountOfCtaCell
} from 'src/components/organisms/Table/TableParts'
import {
  useArchiveDocument,
  useDiscardDocument,
  useListDocumentFilter,
  useListDocuments
} from 'src/fixtures/modules/documents/hooks'
import { useFeatureFlag } from 'src/fixtures/modules/user/hooks'
import { ModalSelectTableColumn, ModalSelectTableColumnOption } from 'src/components/modals/ModalSelectTableColumn'
import { localStorageUtils, StorageKey } from 'src/fixtures/utils/localstorage'
import { useGetOrganization } from 'src/fixtures/modules/organization/hooks'

const DynamicTable = dynamic(() => import('../../organisms/Table'), { ssr: false }) as typeof Table

const columns: TableColumn[] = [
  {
    name: 'title',
    title: '資料名',
    width: 290,
    fixed: true,
    render: ({ row }: DataTypeProvider.ValueFormatterProps) => (
      <DocumentTitleCell imageUrl={row.thumbnail.url} value={row.title} />
    )
  },
  { name: 'createdBy', width: 132, title: '作成者', render: NameCell },
  { name: 'createdAt', width: 152, title: '作成日', render: DatetimeCell },
  { name: 'publicationStatus', width: 86, title: '公開状態', render: PublicationStatusCell },
  {
    name: 'documentFolder',
    width: 120,
    title: 'フォルダ',
    render: ({ row }: DataTypeProvider.ValueFormatterProps) => <FolderNameCell value={row.documentFolder?.name} />
  },
  {
    name: 'labels',
    width: 180,
    title: 'ラベル',
    render: ({ row }: DataTypeProvider.ValueFormatterProps) => <LabelCell labels={row.labels} value={row.label} />
  },
  { name: 'memo', width: 168, title: 'メモ' },
  {
    name: 'countOfContactFromEmail',
    title: 'メール送付済みの人数',
    width: 168,
    sortingEnabled: true,
    render: ({ row }: DataTypeProvider.ValueFormatterProps) => (
      <CountOfContactFromEmailCell value={row.statisticObject?.countOfContactFromEmail} />
    ),
    getCellValue: (row): number => row.statisticObject?.countOfContactFromEmail
  },
  {
    name: 'countOfViewedByContactFromEmail',
    title: 'メール経由の閲覧者数',
    width: 168,
    sortingEnabled: true,
    render: ({ row }: DataTypeProvider.ValueFormatterProps) => (
      <CountOfViewedByContactFromEmailCell value={row.statisticObject?.countOfViewedByContactFromEmail} />
    ),
    getCellValue: (row): number => row.statisticObject?.countOfViewedByContactFromEmail
  },
  {
    name: 'rateOfViewedByContactFromEmail',
    width: 168,
    title: 'メール経由による閲覧率',
    sortingEnabled: true,
    render: ({ row }: DataTypeProvider.ValueFormatterProps) => (
      <RateOfViewedByContactFromEmailCell value={row.statisticObject?.rateOfViewedByContactFromEmail} />
    ),
    getCellValue: (row): number => row.statisticObject?.rateOfViewedByContactFromEmail
  },
  {
    name: 'countOfViewedByPublicLink',
    width: 168,
    title: '公開用URLの閲覧者数',
    sortingEnabled: true,
    render: ({ row }: DataTypeProvider.ValueFormatterProps) => (
      <CountOfViewedByPublicLinkCell value={row.statisticObject?.countOfViewedByPublicLink} />
    ),
    getCellValue: (row): number => row.statisticObject?.countOfViewedByPublicLink
  },
  {
    name: 'secondOfAverageBrowsingTime',
    width: 168,
    title: '一人あたりの閲覧時間',
    sortingEnabled: true,
    render: ({ row }: DataTypeProvider.ValueFormatterProps) => (
      <SecondOfAverageBrowsingTimeCell value={row.statisticObject?.secondOfAverageBrowsingTime} />
    ),
    getCellValue: (row): number => row.statisticObject?.secondOfAverageBrowsingTime
  },
  {
    name: 'countOfOpportunity',
    width: 168,
    title: 'アポイントのクリック数',
    sortingEnabled: true,
    render: ({ row }: DataTypeProvider.ValueFormatterProps) => (
      <CountOfOpportunityCell value={row.statisticObject?.countOfOpportunity} />
    ),
    getCellValue: (row): number => row.statisticObject?.countOfOpportunity
  },
  {
    name: 'rateOfOpportunity',
    width: 168,
    title: 'アポイントのクリック率',
    sortingEnabled: true,
    render: ({ row }: DataTypeProvider.ValueFormatterProps) => (
      <RateOfOpportunityCell value={row.statisticObject?.rateOfOpportunity} />
    ),
    getCellValue: (row): number => row.statisticObject?.rateOfOpportunity
  },
  {
    name: 'countOfCta',
    width: 168,
    title: 'CTAのクリック数',
    sortingEnabled: true,
    render: ({ row }: DataTypeProvider.ValueFormatterProps) => (
      <CountOfCtaCell value={row.statisticObject?.countOfCta} />
    ),
    getCellValue: (row): number => row.statisticObject?.countOfCta
  }
]

const useStyles = makeStyles(() =>
  createStyles({
    DocumentTable: {
      '& .MuiTableCell-body': {
        height: '52px'
      }
    }
  })
)

export const PageDocument = ({ mode }: { mode: 'normal' | 'archive' }) => {
  const classes = useStyles()
  const { page, per, totalPages, setTotalPages, changePage, changePerPage, resetPagination } =
    useTablePagination('document')
  const router = useRouter()
  const currentPage = router.asPath

  // URLからfolder_idを取得
  const selectedFolderId = useMemo(() => {
    const folderId = router.query.folder_id
    return typeof folderId === 'string' ? folderId : null
  }, [router.query.folder_id])

  const [isOpenModal, setIsOpenModal] = useState<boolean>(false)
  const [isOpenModalFolderCreate, setIsOpenModalFolderCreate] = useState<boolean>(false)
  const [isOpenModalFolderEdit, setIsOpenModalFolderEdit] = useState<boolean>(false)
  const [isOpenModalSelectFolder, setIsOpenModalSelectFolder] = useState<boolean>(false)
  const [editingFolder, setEditingFolder] = useState<DocumentFolder | null>(null)
  const [deletingFolder, setDeletingFolder] = useState<DocumentFolder | null>(null)
  const [isOpenModalFolderDelete, setIsOpenModalFolderDelete] = useState<boolean>(false)
  const [isOpenModalSelectTableColumn, setIsOpenModalSelectTableColumn] = useState(false)
  const [storagedColumns, setStoragedColumns] = useState<ModalSelectTableColumnOption[]>([])
  const [reloading] = useState(false)

  // フォルダ一覧取得
  const { data: folderListResponse } = useListDocumentFolders()
  const documentFolders = useMemo(
    () => folderListResponse?.documentFolders ?? [],
    [folderListResponse?.documentFolders]
  )

  // 選択中のフォルダ情報
  const selectedFolder = useMemo(() => {
    if (!selectedFolderId) return null
    return documentFolders.find(f => f.id === selectedFolderId) ?? null
  }, [selectedFolderId, documentFolders])

  // フォルダ管理権限
  const { hasPermission: canManageFolders } = useFolderFeatureFlag('documentFolder', 'create')

  // MEMO: テーブル再描画用のステート
  const [rerendering, setRerendering] = useState(false)

  const [DocumentName, params] = useMemo(() => {
    const params: UserV1DocumentsGetRequest = {
      page: page + 1,
      per,
      isArchived: mode === 'archive' ? true : false,
      orderBy: 'updated_at-desc'
    }
    if (selectedFolderId) {
      params.folderId = selectedFolderId
    }
    const DocumentName = mode === 'archive' ? 'すべて' : 'アーカイブ'
    return [DocumentName, params]
  }, [mode, page, per, selectedFolderId])

  const { data: org } = useGetOrganization()
  const { data: listResponse, mutate } = useListDocuments(params)
  const documentList = listResponse?.documents

  // ドキュメントリストにフォルダ情報を追加
  const enrichedDocumentList = useMemo(() => {
    if (!documentList) return []
    const folderMap = new Map(documentFolders.map(f => [f.id, f]))
    return documentList.map(doc => ({
      ...doc,
      documentFolder: doc.documentFolderId ? folderMap.get(doc.documentFolderId) : undefined
    }))
  }, [documentList, documentFolders])

  const { data: filterData } = useListDocumentFilter()
  const countNormal = filterData?.filter?.normal?.counts ?? 0
  const countArchive = filterData?.filter?.archived?.counts ?? 0

  const folderList = {
    normal: { path: '/documents', count: countNormal },
    archive: {
      path: '/documents?mode=archive',
      count: countArchive
    }
  }

  // フォルダ変更時にページネーションと選択をリセット
  useEffect(() => {
    resetPagination()
    setSelection([])
  }, [selectedFolderId, resetPagination])

  const [selection, setSelection] = useState<Array<number>>([])
  const selectedIds = useMemo<string[]>(() => {
    return selection.map(rowNumber => enrichedDocumentList?.[rowNumber]?.id as string)
  }, [selection, enrichedDocumentList])

  const [confirmOpenModal, setConfirmOpenModal] = useState(false)
  const [deleteConfirmOpenModal, setDeleteConfirmOpenModal] = useState(false)

  const handleClose = useCallback(() => {
    setIsOpenModal(false)
  }, [])

  // フォルダ関連ハンドラー
  const handleAddFolder = useCallback(() => {
    setIsOpenModalFolderCreate(true)
  }, [])
  const handleCloseModalFolderCreate = useCallback(() => {
    setIsOpenModalFolderCreate(false)
  }, [])
  const handleEditFolder = useCallback((folder: DocumentFolder) => {
    setEditingFolder(folder)
    setIsOpenModalFolderEdit(true)
  }, [])
  const handleCloseModalFolderEdit = useCallback(() => {
    setIsOpenModalFolderEdit(false)
    setEditingFolder(null)
  }, [])
  const handleDeleteFolder = useCallback((folder: DocumentFolder) => {
    setDeletingFolder(folder)
    setIsOpenModalFolderDelete(true)
  }, [])
  const handleCloseModalFolderDelete = useCallback(() => {
    setIsOpenModalFolderDelete(false)
    setDeletingFolder(null)
  }, [])

  // フォルダ移動関連
  const { handleMoveDocuments, handleRemoveFromFolder: removeFromFolder } = useMoveDocumentsToFolder()
  const handleOpenModalSelectFolder = useCallback(() => {
    setIsOpenModalSelectFolder(true)
  }, [])
  const handleCloseModalSelectFolder = useCallback(() => {
    setIsOpenModalSelectFolder(false)
  }, [])
  const handleMoveToFolder = useCallback(
    async (folderId: string) => {
      const result = await handleMoveDocuments({ documentIds: selectedIds, documentFolderId: folderId })
      if (result) {
        setIsOpenModalSelectFolder(false)
        setSelection([])
        // テーブルを再マウントして選択状態をリセット
        setRerendering(true)
        setTimeout(() => setRerendering(false), 0)
      }
    },
    [selectedIds, handleMoveDocuments]
  )
  const handleRemoveFromFolder = useCallback(async () => {
    const result = await removeFromFolder(selectedIds)
    if (result) {
      setSelection([])
      // テーブルを再マウントして選択状態をリセット
      setRerendering(true)
      setTimeout(() => setRerendering(false), 0)
    }
  }, [selectedIds, removeFromFolder])

  const {
    handleSaveArchived,
    isLoading: isLoadingArchiveDocument,
    instructions: archiveInstructions
  } = useArchiveDocument(params, mutate)
  const {
    handleDiscardDocuments,
    isLoading: isLoadingDiscardDocument,
    instructions: discardInstructions
  } = useDiscardDocument(mutate)
  const [openArchiveAlert, setOpenArchiveAlert] = useState(false)
  const [openDeleteAlert, setOpenDeleteAlert] = useState(false)

  // アーカイブエラー時のモーダル表示
  useEffect(() => {
    if (archiveInstructions?.includes('document_belongs_to_site')) {
      setOpenArchiveAlert(true)
    }
  }, [archiveInstructions])

  // 削除エラー時のモーダル表示
  useEffect(() => {
    if (discardInstructions?.includes('document_belongs_to_site')) {
      setOpenDeleteAlert(true)
    }
  }, [discardInstructions])

  const handleClickArchiveCloseButton = useCallback(() => {
    setSelection([])
  }, [])
  const handleCloseModal = useCallback(() => {
    setConfirmOpenModal(false)
    setSelection([])
  }, [])
  const handleClickModalOkButton = useCallback(
    (isArchived: boolean) => async () => {
      if (!selectedIds || selectedIds.length == 0) {
        return
      }
      await handleSaveArchived(selectedIds, isArchived)
      handleCloseModal()
      // テーブルを再マウントして選択状態をリセット
      setRerendering(true)
      setTimeout(() => setRerendering(false), 0)
    },
    [selectedIds, handleSaveArchived, handleCloseModal]
  )

  const handleClickDeleteOkButton = useCallback(async () => {
    if (!selectedIds || selectedIds.length === 0) return
    if (isLoadingDiscardDocument) return
    await handleDiscardDocuments(selectedIds)
    setDeleteConfirmOpenModal(false)
    setSelection([])
    // テーブルを再マウントして選択状態をリセット
    setRerendering(true)
    setTimeout(() => setRerendering(false), 0)
  }, [selectedIds, handleDiscardDocuments, isLoadingDiscardDocument])

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteConfirmOpenModal(false)
  }, [])

  const customButtonList: CustomButtonListType[] = useMemo(() => {
    const buttons: CustomButtonListType[] = []

    if (mode === 'archive') {
      buttons.push({ buttonTitle: 'アーカイブから戻す', onConfirm: () => setConfirmOpenModal(true) })
    } else {
      // フォルダ内表示時は「フォルダから解除」、それ以外は「フォルダへ移動」
      if (selectedFolderId) {
        buttons.push({ showIcon: 'folder', buttonTitle: 'フォルダから解除', onConfirm: handleRemoveFromFolder })
      } else {
        buttons.push({ showIcon: 'folder', buttonTitle: 'フォルダへ移動', onConfirm: handleOpenModalSelectFolder })
      }
      buttons.push({ showIcon: 'archive', buttonTitle: 'アーカイブ', onConfirm: () => setConfirmOpenModal(true) })
    }
    buttons.push({ showIcon: 'delete', buttonTitle: '削除', onConfirm: () => setDeleteConfirmOpenModal(true) })

    return buttons
  }, [mode, selectedFolderId, handleRemoveFromFolder, handleOpenModalSelectFolder])

  const showDialog = useMemo(() => {
    return (
      <Box position="absolute" bottom="50px" px="16px" width={1} zIndex={999}>
        <SelectTableRow
          count={selectedIds.length}
          onClose={handleClickArchiveCloseButton}
          customButtonList={customButtonList}
        />
      </Box>
    )
  }, [selectedIds, handleClickArchiveCloseButton, customButtonList])

  const isShowTable = useMemo(
    () =>
      !reloading &&
      !isLoadingArchiveDocument &&
      !isLoadingDiscardDocument &&
      storagedColumns.length > 0 &&
      columns.length > 0,
    [reloading, columns.length, storagedColumns.length, isLoadingArchiveDocument, isLoadingDiscardDocument]
  )

  // MEMO: @snuffy ページマウント時に意図的に再取得してあげる(revalidate を防ぐため)
  useEffect(() => {
    mutate()
  }, [mutate])

  useEffect(() => {
    resetPagination()
    mutate()
  }, [mode, mutate, resetPagination])

  useEffect(() => {
    if (listResponse?.pageInfo?.totalPages && listResponse?.pageInfo?.totalPages !== totalPages) {
      setTotalPages(listResponse?.pageInfo?.totalPages)
    }
  }, [listResponse?.pageInfo?.totalPages, setTotalPages, totalPages])

  useEffect(() => {
    if (!org?.organization?.id) {
      return
    }
    const res = localStorageUtils.get<ModalSelectTableColumnOption[]>(
      StorageKey.DOCUMENT_TABLE_COLUMNS(org.organization.id)
    )
    if (res) {
      const newStoragedColumns = columns
        // MEMO: local に保存したデータにあれば取得、なければ column から生成を行って表示する
        .map(columnItem => {
          const item = res.find(localItem => columnItem.name === localItem.name)
          return item || { ...columnItem, checked: true }
        })
        // MEMO: local に保存した順番に並び替え
        .sort((a, b) => {
          const aIndex = res.findIndex(item => item.name === a.name)
          const bIndex = res.findIndex(item => item.name === b.name)
          if (aIndex === -1 || bIndex === -1) return 0
          return aIndex - bIndex
        })

      setStoragedColumns(newStoragedColumns)
    } else {
      setStoragedColumns(columns.map(column => ({ ...column, checked: true })))
    }
  }, [columns, org?.organization?.id])

  const onConfirmModalSelectTableColumn = useCallback(
    (result: ModalSelectTableColumnOption[]) => {
      if (org?.organization?.id) {
        localStorageUtils.set(StorageKey.DOCUMENT_TABLE_COLUMNS(org.organization.id), result)
      }

      // MEMO: テーブルのカラムが増える場合 runtime error を起こす場合があるので、table の再描画前に一度消す
      setRerendering(true)
      setStoragedColumns(result)
      setTimeout(() => {
        setRerendering(false)
      }, 32)
    },
    [org?.organization?.id]
  )

  const handleUpdateTableColumnWidth = useCallback(
    (nextColumnWidths: TableColumnWidthInfo[]) => {
      if (org?.organization?.id) {
        const next: ModalSelectTableColumnOption[] = storagedColumns.map(item => {
          const find = nextColumnWidths.find(i => i.columnName === item.name)
          if (find) {
            return { ...item, width: Number(find.width) }
          }
          return item
        })
        setStoragedColumns(next)
        localStorageUtils.set(StorageKey.DOCUMENT_TABLE_COLUMNS(org.organization.id), next)
      }
    },
    [org?.organization?.id, storagedColumns]
  )

  const { hasPermission: isCreatable } = useFeatureFlag('document', 'create')

  return (
    <>
      <Layout>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box bgcolor={Colors.functional.background.default} borderBottom={`1px solid ${Colors.background.silver}`}>
            <Header
              iconSize="28px"
              icon="document"
              title="資料"
              rightSection={
                <Button
                  title="資料の登録"
                  startIcon={<DynamicMuiIcon variant="add" />}
                  onClick={() => isCreatable && setIsOpenModal(true)}
                  disabled={!isCreatable}
                />
              }
            />
          </Box>
          <Box height="100%" flexGrow={1} display="flex" overflow="hidden">
            <Box
              flexShrink={0}
              width="215px"
              bgcolor={Colors.functional.background.default}
              height={1}
              borderRight={`1px solid ${Colors.background.silver}`}
            >
              <SubSideFolderList
                folderList={folderList}
                currentPage={currentPage}
                documentFolders={documentFolders}
                selectedFolderId={selectedFolderId}
                canManageFolders={canManageFolders}
                onAddFolder={handleAddFolder}
                onEditFolder={handleEditFolder}
                onDeleteFolder={handleDeleteFolder}
              />
            </Box>
            <Box position="relative" width={1} height={1} pt="16px" overflow="auto" className={classes.DocumentTable}>
              {/* フォルダ選択時のヘッダー表示 */}
              {selectedFolder && (
                <Box px="24px" pb="16px" display="flex" alignItems="center">
                  <Box color={Colors.base.middleGray} position="relative" top="2px">
                    <DynamicMuiIcon variant="folder" size="20px" />
                  </Box>
                  <Box ml="8px" />
                  <Box fontSize="16px" fontWeight="bold" color={Colors.base.black}>
                    {selectedFolder.name}
                  </Box>
                  <Box ml="8px" />
                  <Box fontSize="14px" color={Colors.base.middleGray}>
                    {selectedFolder.documentsCount}件
                  </Box>
                </Box>
              )}
              {!rerendering && isShowTable && (
                <DynamicTable
                  columns={storagedColumns
                    .filter(item => item.checked)
                    .map(({ checked: _checked, name, width }) => {
                      return { ...columns.find(item => item.name === name), width } as TableColumn
                    })
                    .filter(Boolean)}
                  rows={enrichedDocumentList}
                  onClickRow={row => router.push('/documents/[documentId]', `/documents/${row.id}`)}
                  selectable
                  selection={selection}
                  setSelection={setSelection as TableProps<SelectionStateProps>['setSelection']}
                  page={page}
                  per={per}
                  totalPages={totalPages}
                  changePage={page => {
                    changePage(page)
                    setSelection([])
                  }}
                  changePerPage={perPage => {
                    changePerPage(perPage)
                    setSelection([])
                  }}
                  numberOfRows={listResponse?.pageInfo?.totalCount}
                  showTotalCount
                  onOpenModalSelectTableColumn={() => setIsOpenModalSelectTableColumn(true)}
                  handleUpdateTableColumnWidth={handleUpdateTableColumnWidth}
                  droppable
                />
              )}
              {selectedIds.length > 0 && showDialog}
            </Box>
          </Box>
        </Box>
        <ModalAlertShare
          open={openArchiveAlert}
          onClose={() => setOpenArchiveAlert(false)}
          title="アーカイブできません"
          description="選択した資料が資料サイトに含まれているため、アーカイブできません"
          infoList={[
            {
              question: 'この資料をアーカイブするには？',
              answerList: [{ label: '資料サイトからこの資料を削除する' }, { label: '資料サイト自体をアーカイブする' }]
            }
          ]}
        />
        <ModalAlertShare
          open={openDeleteAlert}
          onClose={() => setOpenDeleteAlert(false)}
          title="削除できません"
          description="選択した資料が資料サイトに含まれているため、削除できません"
          infoList={[
            {
              question: 'この資料を削除するには？',
              answerList: [{ label: '資料サイトからこの資料を削除する' }]
            }
          ]}
        />
        <ModalDocumentRegister open={isOpenModal} onConfirm={() => {}} onClose={handleClose} onCancel={handleClose} />
        <ModalFolderCreate open={isOpenModalFolderCreate} onClose={handleCloseModalFolderCreate} />
        <ModalFolderEdit open={isOpenModalFolderEdit} folder={editingFolder} onClose={handleCloseModalFolderEdit} />
        <ModalFolderDelete
          open={isOpenModalFolderDelete}
          folder={deletingFolder}
          onClose={handleCloseModalFolderDelete}
        />
        <ModalSelectFolder
          open={isOpenModalSelectFolder}
          folders={documentFolders}
          selectedCount={selectedIds.length}
          onClose={handleCloseModalSelectFolder}
          onConfirm={handleMoveToFolder}
        />
        <PopupTemplate
          open={confirmOpenModal}
          title={`資料を${customButtonList[0].buttonTitle}`}
          subTitle={`${selectedIds.length}件  選択中`}
          onClose={handleCloseModal}
          hasClose
          hasCancel
          hasConfirm
          onConfirm={handleClickModalOkButton(mode === 'archive')}
          cancelButtonLabel="キャンセル"
          confirmButtonLabel="OK"
        >
          <Box p="24px">
            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
              {`選択した資料を${DocumentName}へ移動します\n本当によろしいですか？`}
            </Typography>
          </Box>
        </PopupTemplate>
        <PopupTemplate
          open={deleteConfirmOpenModal}
          title="資料を削除"
          subTitle={`${selectedIds.length}件  選択中`}
          onClose={handleCloseDeleteModal}
          hasClose
          hasCancel
          hasConfirm
          onConfirm={handleClickDeleteOkButton}
          cancelButtonLabel="キャンセル"
          confirmButtonLabel="削除する"
          confirmButtonKind="negative"
        >
          <Box p="24px">
            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
              {'選択した資料を削除します。\nこの操作は取り消すことができません。\n本当によろしいですか？'}
            </Typography>
          </Box>
        </PopupTemplate>
      </Layout>
      <ModalSelectTableColumn
        initialColumnItems={storagedColumns}
        open={isOpenModalSelectTableColumn}
        onClose={() => setIsOpenModalSelectTableColumn(false)}
        onCancel={() => setIsOpenModalSelectTableColumn(false)}
        onConfirm={onConfirmModalSelectTableColumn}
        hideSettings
      />
    </>
  )
}
